import { useEffect, useState } from "react";
import apiService from "../api/apiService";

/**
 * [N-6, polecenie właściciela 2026-09-08] ZASIĘG „MÓJ DZIAŁ” DLA MENU.
 *
 * Serwer (`GET /my-dept/scope`) mówi, czy wołający ma ZASIĘG OPERACYJNY (własny zespół albo działy, których jest
 * kierownikiem), czy jedynie działy, które do niego RAPORTUJĄ. W tym drugim przypadku hub i dropdown w Sidebarze
 * pokazują wyłącznie Pulpit / Wnioski / Strukturę — cytat: „cała historia operacji firmy w nie jego dziale go nie
 * dotyczy (…) nie musimy zdradzać wszystkich operacji w zakresie funkcjonowania działu".
 *
 * Jedno pobranie na sesję (pamięć modułu): Sidebar renderuje się przy każdej nawigacji, a odpowiedź zmienia się
 * dopiero ze zmianą struktury. ⚠️ Awaria albo 403 = ZASIĘG PEŁNY (`maZasiegOperacyjny: true`): menu nie może
 * chować zakładek kierownikowi tylko dlatego, że jedno zapytanie nie doszło — serwer i tak liczy dane po zasięgu.
 *
 * ─────────────────────────────────────────────────────────────────────────────────────────────────────────────
 * [WYDAJNOŚĆ 2026-09-12] TA SAMA PAMIĘĆ OBSŁUGUJE TERAZ TAKŻE `useDeptHierarchy`.
 *
 * CO ZMIERZYŁEM NA PRODUKCJI (dziennik, 3 dni): `GET /api/my-dept/scope` poszło 374 razy — praktycznie tyle,
 * co lista zgłoszeń. Menu pytało raz (ta pamięć działa od N-6), ale `useDeptHierarchy` — wspólny hak DZIESIĘCIU
 * stron „Mojego Działu" — wołał ten sam adres BEZPOŚREDNIO, przy każdym montażu strony. Przeskok między
 * zakładkami modułu to montaż nowej strony, więc kierownik klikający po zakładkach wypuszczał to zapytanie
 * za każdym razem, a przy wejściu do aplikacji DWA razy (tożsamość `user` zmienia się dwukrotnie — patrz
 * `AuthContext.checkLoggedIn`).
 *
 * Dlatego pamięć trzyma teraz SUROWĄ odpowiedź serwera (`pobierzZasiegDzialu`), a nie tylko trzy pola potrzebne
 * menu — `useDeptHierarchy` używa z niej `departmentIds`, `userIds`, `isSupervisor`, `canManage`, `rola`…
 *
 * ⚠️ BEZPIECZEŃSTWO: zasięg mówi, CZYICH danych kierownik może zobaczyć na ekranie (serwer i tak liczy własny
 * zakres przy każdym zapytaniu o dane — to jest warstwa interfejsu). Pamięć jest więc czyszczona:
 *   • przy wylogowaniu (`AuthContext.logout`),
 *   • przy zmianie uprawnień w locie (zdarzenie `uprawnienia-zmienione`, obsługiwane w `AuthContext`),
 *   • przy zmianie struktury działu i przełożonego (`dept-structure-changed`, `supervisor-changed` —
 *     zdarzenia okna wystawiane przez `NotificationContext` z SignalR),
 *   • przez `refresh()` w `useDeptHierarchy` (przycisk odświeżenia na stronach modułu).
 */

/** Znormalizowany kształt dla menu (stała tożsamość — Sidebar liczy z niego `sections` w useMemo). */
let pamiec = null;
/** Surowa odpowiedź serwera opakowana w `{ dane }`; `null` = jeszcze nie pytaliśmy. */
let pamiecSurowa = null;
/** Żądanie w locie — kolejni konsumenci doklejają się zamiast pytać drugi raz. */
let wTrakcie = null;
/** Numer pokolenia pamięci; rośnie przy unieważnieniu (odcina spóźnione odpowiedzi). */
let wersja = 0;
const sluchacze = new Set();

/** Bieżące pokolenie pamięci — konsument trzyma je w stanie, żeby unieważnienie odpaliło mu efekt. */
export const wersjaPamieciZasiegu = () => wersja;

export const wyczyscPamiecZasiegu = () => {
    pamiec = null;
    pamiecSurowa = null;
    wTrakcie = null;
    wersja += 1;
    sluchacze.forEach((fn) => {
        try { fn(wersja); } catch { /* konsument nie może uciszyć pozostałych */ }
    });
};

/** Zapisuje się na unieważnienia pamięci. Zwraca funkcję wypisania. */
export const subskrybujZasiegDzialu = (fn) => {
    if (typeof fn !== "function") return () => {};
    sluchacze.add(fn);
    return () => { sluchacze.delete(fn); };
};

/**
 * Surowa odpowiedź `/my-dept/scope` na tę sesję (albo `null`, gdy zapytanie padło).
 * Awaria NIE jest zapamiętywana (patrz wyjaśnienie przy zapisie niżej): z tej pamięci czyta także
 * `useDeptHierarchy`, gdzie brak wyniku oznacza PUSTY zasięg, a nie pełny.
 */
export const pobierzZasiegDzialu = () => {
    if (pamiecSurowa !== null) return Promise.resolve(pamiecSurowa.dane);
    if (!wTrakcie) {
        const mojeWydanie = wersja;
        wTrakcie = Promise.resolve()
            .then(() => apiService.getMyDeptScope())
            .then((r) => r?.data || null)
            .catch(() => null)
            .then((dane) => {
                // Odpowiedź z poprzedniego pokolenia (ktoś w międzyczasie unieważnił pamięć) trafia
                // do TEGO konsumenta, ale nie zostaje stanem sesji.
                if (mojeWydanie === wersja) {
                    /* [PRZEGLAD ADWERSARIALNY 2026-09-12] AWARII NIE ZAPAMIETUJEMY.
                       Gdy z tej pamieci czytalo tylko MENU, zapamietana awaria byla nieszkodliwa: menu
                       przyjmuje wtedy zasieg pelny (fail-open). Odkad czyta z niej takze `useDeptHierarchy`,
                       ten sam `null` mapuje sie na PUSTY zasieg (fail-closed) — czyli jedno mrugniecie sieci
                       przy wejsciu do aplikacji oslepialoby kierownikowi WSZYSTKIE strony „Mojego Dzialu”
                       (pusty zespol, puste listy) do konca zycia karty. Przy braku wyniku zostawiamy pamiec
                       pusta: nastepne wejscie zapyta jeszcze raz. */
                    if (dane !== null) pamiecSurowa = { dane };
                    wTrakcie = null;
                }
                return dane;
            });
    }
    return wTrakcie;
};

// Unieważnienie sterowane zdarzeniami na żywo: zmiana struktury działu albo przełożonego przestawia
// zasięg, więc menu i strony modułu muszą go policzyć na nowo bez czekania na F5.
if (typeof window !== "undefined") {
    window.addEventListener("dept-structure-changed", wyczyscPamiecZasiegu);
    window.addEventListener("supervisor-changed", wyczyscPamiecZasiegu);
}

// [J-5] `trybZakladek` = kod zestawu zakładek z serwera („pelny" / „tylko-raportujacy" / „przelozony-dyrektora"); brak = jak dotąd.
// [O2] `dzialy` = działy odpowiedzialności z serwera (rola, tryb i zakładki per dział) — przełącznik działów w hubie.
const ZASIEG_PELNY = { maZasiegOperacyjny: true, trybZakladek: null, dzialyRaportujace: [], dzialy: [], gotowe: false };

const zNormalizuj = (d) => ({
    maZasiegOperacyjny: d?.hasOperationalScope !== false,
    trybZakladek: typeof d?.trybZakladek === "string" ? d.trybZakladek : null,
    dzialyRaportujace: Array.isArray(d?.raportujaceDzialy) ? d.raportujaceDzialy : [],
    dzialy: Array.isArray(d?.dzialy) ? d.dzialy : [],
    gotowe: true,
});

const useMojDzialZasieg = (aktywny = true) => {
    const [stan, setStan] = useState(() => pamiec || ZASIEG_PELNY);
    // Pokolenie pamięci — po unieważnieniu efekt niżej pyta jeszcze raz.
    const [wydanie, setWydanie] = useState(wersjaPamieciZasiegu);
    useEffect(() => subskrybujZasiegDzialu(setWydanie), []);

    useEffect(() => {
        if (!aktywny) return undefined;
        let zywy = true;
        pobierzZasiegDzialu().then((d) => {
            // Tożsamość obiektu musi być STAŁA: Sidebar przelicza z niego `sections` w useMemo,
            // więc nowy obiekt przy każdym montażu odbudowywałby całe menu bez powodu.
            if (!pamiec) pamiec = zNormalizuj(d);
            if (zywy) setStan(pamiec);
        });
        return () => { zywy = false; };
    }, [aktywny, wydanie]);

    return stan;
};

export default useMojDzialZasieg;
